import React, { useEffect, useRef } from 'react';
import { EditorView, Decoration, ViewPlugin, WidgetType, keymap } from '@codemirror/view';
import { EditorState } from '@codemirror/state';
import { markdown } from '@codemirror/lang-markdown';
import { defaultKeymap, history, historyKeymap } from '@codemirror/commands';

// ---------------------------------------------------------------------------
// Widget-Klassen für Inline-Formatierung (wie LiveEditor)
// ---------------------------------------------------------------------------
class BoldWidget extends WidgetType {
  constructor(text) { super(); this.text = text; }
  toDOM() { const el = document.createElement('strong'); el.textContent = this.text; return el; }
  eq(other) { return other instanceof BoldWidget && other.text === this.text; }
}

class ItalicWidget extends WidgetType {
  constructor(text) { super(); this.text = text; }
  toDOM() { const el = document.createElement('em'); el.textContent = this.text; return el; }
  eq(other) { return other instanceof ItalicWidget && other.text === this.text; }
}

class MarkWidget extends WidgetType {
  constructor(text) { super(); this.text = text; }
  toDOM() {
    const el = document.createElement('mark');
    el.textContent = this.text;
    el.style.cssText = 'background:var(--highlight-bg);color:var(--text-primary);border-radius:3px;padding:1px 3px;font-style:normal';
    return el;
  }
  eq(other) { return other instanceof MarkWidget && other.text === this.text; }
}

// ---------------------------------------------------------------------------
// Live-Preview-Dekorationen — wie LiveEditor, aber leichter
// ---------------------------------------------------------------------------
function buildDecos(view) {
  const widgets = [];
  const doc = view.state.doc;
  const hasFocus = view.hasFocus;
  const cursorLine = hasFocus ? doc.lineAt(view.state.selection.main.head).number : -1;

  for (let i = 1; i <= doc.lines; i++) {
    const line = doc.line(i);
    const text = line.text;
    if (i === cursorLine) continue; // Aktive Zeile zeigt roh-Markdown

    // **Fett**
    for (const m of text.matchAll(/\*\*([^*\n]+)\*\*/g)) {
      const from = line.from + m.index;
      const captured = m[1];
      widgets.push(Decoration.replace({ widget: new BoldWidget(captured) }).range(from, from + m[0].length));
    }

    // *Kursiv* (nicht **)
    for (const m of text.matchAll(/(?<!\*)\*([^*\n]+)\*(?!\*)/g)) {
      const from = line.from + m.index;
      const captured = m[1];
      widgets.push(Decoration.replace({ widget: new ItalicWidget(captured) }).range(from, from + m[0].length));
    }

    // ==Hervorhebung==
    for (const m of text.matchAll(/==([^=\n]+)==/g)) {
      const from = line.from + m.index;
      const captured = m[1];
      widgets.push(Decoration.replace({ widget: new MarkWidget(captured) }).range(from, from + m[0].length));
    }
  }

  widgets.sort((a, b) => a.from - b.from || a.to - b.to);
  return Decoration.set(widgets, true);
}

const decoPlugin = ViewPlugin.define(
  (view) => ({
    decorations: buildDecos(view),
    update(u) {
      if (u.docChanged || u.selectionSet || u.focusChanged) this.decorations = buildDecos(u.view);
    },
  }),
  { decorations: (v) => v.decorations }
);

// ---------------------------------------------------------------------------
// HighlightEditor — Haupt-Komponente
// ---------------------------------------------------------------------------
export default function HighlightEditor({ value, onSave, onDiscard, onPreview, onBlurWithChanges }) {
  const containerRef = useRef(null);
  const saveRef = useRef(onSave);
  const discardRef = useRef(onDiscard);
  const previewRef = useRef(onPreview);
  const blurRef = useRef(onBlurWithChanges);
  const previewTimer = useRef(null);
  const origRef = useRef(value);

  // Callbacks aktuell halten ohne den Effekt neu zu starten
  useEffect(() => { saveRef.current = onSave; }, [onSave]);
  useEffect(() => { discardRef.current = onDiscard; }, [onDiscard]);
  useEffect(() => { previewRef.current = onPreview; }, [onPreview]);
  useEffect(() => { blurRef.current = onBlurWithChanges; }, [onBlurWithChanges]);

  useEffect(() => {
    if (!containerRef.current) return;

    const view = new EditorView({
      state: EditorState.create({
        doc: value,
        extensions: [
          history(),
          EditorView.lineWrapping,
          markdown(),
          decoPlugin,
          keymap.of([
            // * mit Selektion: plain→italic, italic→bold, bold→italic (wie LiveEditor)
            { key: '*', run: (view) => {
              const { from, to } = view.state.selection.main;
              if (from === to) return false;
              const selected = view.state.sliceDoc(from, to);
              if (selected.startsWith('**') && selected.endsWith('**') && selected.length >= 5) {
                const inner = selected.slice(2, -2);
                view.dispatch({ changes: { from, to, insert: `*${inner}*` }, selection: { anchor: from, head: from + inner.length + 2 } });
                return true;
              }
              if (selected.startsWith('*') && selected.endsWith('*') && selected.length >= 3) {
                const inner = selected.slice(1, -1);
                view.dispatch({ changes: { from, to, insert: `**${inner}**` }, selection: { anchor: from, head: from + inner.length + 4 } });
                return true;
              }
              view.dispatch({ changes: { from, to, insert: `*${selected}*` }, selection: { anchor: from, head: to + 2 } });
              return true;
            }},
            // = mit Selektion: 1. Druck → =text=, 2. Druck → ==text==, auf ==text== → unwrap (wie LiveEditor)
            { key: '=', run: (view) => {
              const { from, to } = view.state.selection.main;
              if (from === to) return false;
              const selected = view.state.sliceDoc(from, to);
              if (selected.startsWith('==') && selected.endsWith('==') && selected.length >= 5) {
                const inner = selected.slice(2, -2);
                view.dispatch({ changes: { from, to, insert: inner }, selection: { anchor: from, head: from + inner.length } });
                return true;
              }
              const charBefore = from > 0 ? view.state.sliceDoc(from - 1, from) : '';
              const charAfter  = to < view.state.doc.length ? view.state.sliceDoc(to, to + 1) : '';
              if (charBefore === '=' && charAfter === '=') {
                view.dispatch({ changes: { from: from - 1, to: to + 1, insert: `==${selected}==` }, selection: { anchor: from - 1, head: from - 1 + selected.length + 4 } });
                return true;
              }
              view.dispatch({ changes: { from, to, insert: `=${selected}=` }, selection: { anchor: from + 1, head: from + 1 + selected.length } });
              return true;
            }},
            // ⌘B = Bold toggle
            { key: 'Mod-b', run: (view) => {
              const { from, to } = view.state.selection.main;
              if (from === to) return false;
              const selected = view.state.sliceDoc(from, to);
              if (selected.startsWith('**') && selected.endsWith('**') && selected.length >= 5) {
                const inner = selected.slice(2, -2);
                view.dispatch({ changes: { from, to, insert: inner }, selection: { anchor: from, head: from + inner.length } });
              } else {
                view.dispatch({ changes: { from, to, insert: `**${selected}**` }, selection: { anchor: from, head: from + selected.length + 4 } });
              }
              return true;
            }},
            // ⌘I = Italic toggle
            { key: 'Mod-i', run: (view) => {
              const { from, to } = view.state.selection.main;
              if (from === to) return false;
              const selected = view.state.sliceDoc(from, to);
              if (selected.startsWith('*') && !selected.startsWith('**') && selected.endsWith('*') && !selected.endsWith('**') && selected.length >= 3) {
                const inner = selected.slice(1, -1);
                view.dispatch({ changes: { from, to, insert: inner }, selection: { anchor: from, head: from + inner.length } });
              } else {
                view.dispatch({ changes: { from, to, insert: `*${selected}*` }, selection: { anchor: from, head: from + selected.length + 2 } });
              }
              return true;
            }},
            // ⌘⇧H = Hervorhebung (==text==) toggle
            { key: 'Mod-Shift-h', run: (view) => {
              const { from, to } = view.state.selection.main;
              if (from === to) return false;
              const selected = view.state.sliceDoc(from, to);
              if (selected.startsWith('==') && selected.endsWith('==') && selected.length >= 5) {
                const inner = selected.slice(2, -2);
                view.dispatch({ changes: { from, to, insert: inner }, selection: { anchor: from, head: from + inner.length } });
              } else {
                view.dispatch({ changes: { from, to, insert: `==${selected}==` }, selection: { anchor: from, head: from + selected.length + 4 } });
              }
              return true;
            }},
            // ⌘Enter = Speichern + Editor schließen
            { key: 'Mod-Enter', run: (view) => {
              clearTimeout(previewTimer.current);
              saveRef.current(view.state.doc.toString());
              discardRef.current(null); // null = nicht verwerfen, sondern schließen nach Save
              return true;
            }},
            ...defaultKeymap,
            ...historyKeymap,
            { key: 'Escape', run: () => { discardRef.current(origRef.current); return true; } },
          ]),
          EditorView.updateListener.of((upd) => {
            // Echtzeit-Preview bei Eingabe (100ms Debounce)
            if (upd.docChanged) {
              clearTimeout(previewTimer.current);
              previewTimer.current = setTimeout(() => {
                if (previewRef.current) previewRef.current(upd.view.state.doc.toString());
              }, 100);
            }
            // Blur: Wenn ungespeicherte Änderungen → Inline-Leiste zeigen
            if (upd.focusChanged && !upd.view.hasFocus) {
              clearTimeout(previewTimer.current);
              const currentText = upd.view.state.doc.toString();
              if (currentText !== origRef.current && blurRef.current) {
                blurRef.current();
              }
            }
          }),
          EditorView.theme({
            '&': { background: 'transparent', outline: 'none' },
            '.cm-content': {
              fontFamily: 'var(--font-sans)',
              fontSize: 'var(--text-sm)',
              color: 'var(--color-text-secondary)',
              fontStyle: 'italic',
              lineHeight: '1.65',
              padding: '0',
              caretColor: 'var(--color-primary)',
            },
            '.cm-line': { padding: '0' },
            '.cm-cursor': { borderLeftColor: 'var(--color-primary)', borderLeftWidth: '2px' },
            '.cm-selectionBackground': {
              background:
                'color-mix(in srgb, var(--color-primary) 20%, transparent) !important',
            },
            '&.cm-focused .cm-selectionBackground': {
              background:
                'color-mix(in srgb, var(--color-primary) 25%, transparent) !important',
            },
            '.cm-gutters': { display: 'none' },
            '.cm-scroller': { overflow: 'visible' },
          }),
        ],
      }),
      parent: containerRef.current,
    });

    // Fokus setzen und Cursor ans Ende
    view.focus();
    view.dispatch({ selection: { anchor: value.length } });

    return () => {
      clearTimeout(previewTimer.current);
      view.destroy();
    };
  }, []); // eslint-disable-line

  return <div ref={containerRef} className="hl-inline-editor-cm" />;
}
